import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { accessRequestService } from "../api/services/accessRequestService";
import { useFormSubmit } from "../hooks/useFormSubmit";
import { MAX_ABOUT_LENGTH, MAX_NAME_LENGTH, validateAccessRequest, type FieldErrors, type AccessRequestFields } from "../utils/validation";
import { CheckboxField, HoneypotField, TextAreaField, TextField } from "./FormFields";
import { FormSuccess } from "./FormSuccess";
import "./styles/Form.css";

interface AccessRequestFormState extends AccessRequestFields {
  website: string;
}

const EMPTY_FORM: AccessRequestFormState = { name: "", email: "", about: "", consent: false, website: "" };

export function AccessRequestForm() {
  const [form, setForm] = useState<AccessRequestFormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FieldErrors<keyof AccessRequestFields>>({});
  const { status, errorMessage, submit } = useFormSubmit(accessRequestService.create);

  function handleFieldChange(field: string, value: string | boolean) {
    setForm((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: undefined }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const found = validateAccessRequest(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    void submit({ ...form, name: form.name.trim(), email: form.email.trim(), about: form.about.trim() });
  }

  if (status === "success") {
    return (
      <FormSuccess
        title="Thanks, your request is in"
        message="We'll take a look and email you at the address you gave us as soon as your access is ready."
      />
    );
  }

  return (
    <form className="form" onSubmit={handleSubmit} noValidate>
      <TextField id="access-name" name="name" label="Your name" value={form.name} onChange={handleFieldChange} autoComplete="name" maxLength={MAX_NAME_LENGTH} error={errors.name} />
      <TextField id="access-email" name="email" type="email" label="Email address" value={form.email} onChange={handleFieldChange} autoComplete="email" error={errors.email} hint="We'll send your invitation here." />
      <TextAreaField id="access-about" name="about" label="Tell us a little about your family" value={form.about} onChange={handleFieldChange} maxLength={MAX_ABOUT_LENGTH} error={errors.about} isOptional hint="What would you like SENfully to help with? Please leave out your child's name and any medical details." />
      <HoneypotField value={form.website} onChange={handleFieldChange} />
      <CheckboxField id="access-consent" name="consent" checked={form.consent} onChange={handleFieldChange} error={errors.consent}>
        I'm happy for SENfully to contact me about my request. Read our <Link to="/privacy">privacy notice</Link>.
      </CheckboxField>

      {status === "error" && <p className="form__banner" role="alert">{errorMessage}</p>}

      <button type="submit" className="btn btn--primary btn--lg form__submit" disabled={status === "submitting"}>
        {status === "submitting" ? "Sending…" : "Request access"}
      </button>
    </form>
  );
}
